import { z } from 'zod';
import type { ReviewSummaryDTO } from './reviews.js';

/**
 * Reviews of a single product, shown on its page. Staff add them and decide which are shown
 * (Admin > Catalog > Product reviews); customers see only the approved ones.
 */
export const PRODUCT_REVIEW_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type ProductReviewStatus = (typeof PRODUCT_REVIEW_STATUSES)[number];

export const PRODUCT_REVIEW_AUTHOR_MAX = 80;
export const PRODUCT_REVIEW_TITLE_MAX = 120;
export const PRODUCT_REVIEW_BODY_MAX = 1500;
/** How many reviews a product page loads at a time. */
export const PRODUCT_REVIEWS_PAGE_SIZE = 5;

const fields = {
  authorName: z.string().trim().min(1, 'Add a name').max(PRODUCT_REVIEW_AUTHOR_MAX),
  rating: z.number().int().min(1, 'Pick a star rating').max(5),
  title: z.string().trim().max(PRODUCT_REVIEW_TITLE_MAX).default(''),
  body: z
    .string()
    .trim()
    .min(1, 'Add what they said')
    .max(PRODUCT_REVIEW_BODY_MAX, `Keep it under ${PRODUCT_REVIEW_BODY_MAX} characters`),
  status: z.enum(PRODUCT_REVIEW_STATUSES),
};

/** What a signed-in customer sends from the product page. It always starts "pending": staff approve it before it shows. */
export const CUSTOMER_REVIEW_MIN_BODY = 10;
export const customerReviewSchema = z.object({
  authorName: fields.authorName,
  rating: fields.rating,
  title: fields.title,
  body: z
    .string()
    .trim()
    .min(CUSTOMER_REVIEW_MIN_BODY, `Write a few words (at least ${CUSTOMER_REVIEW_MIN_BODY} characters)`)
    .max(PRODUCT_REVIEW_BODY_MAX, `Keep it under ${PRODUCT_REVIEW_BODY_MAX} characters`),
});
export type CustomerReviewInput = z.infer<typeof customerReviewSchema>;

/** The signed-in customer's own review of a product, if they wrote one. */
export interface MyProductReviewDTO {
  review: { status: ProductReviewStatus; rating: number; title: string; body: string; createdAt: string } | null;
}

export const productReviewCreateSchema = z.object({
  productId: z.string().uuid(),
  ...fields,
  status: fields.status.default('approved'),
});
export type ProductReviewCreateInput = z.infer<typeof productReviewCreateSchema>;

export const productReviewUpdateSchema = z
  .object({
    authorName: fields.authorName,
    rating: fields.rating,
    title: fields.title,
    body: fields.body,
    status: fields.status,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to change' });
export type ProductReviewUpdateInput = z.infer<typeof productReviewUpdateSchema>;

/** What customers see on a product page. */
export interface ProductReviewDTO {
  id: string;
  author: string;
  rating: number;
  title: string;
  body: string;
  createdAt: string;
  /** the reviewer bought this product from us */
  verified: boolean;
}

export interface ProductReviewsDTO {
  items: ProductReviewDTO[];
  /** over every approved review of the product, whichever star filter is on; `minRating` is 1 (all are shown) */
  summary: ReviewSummaryDTO;
  pagination: { page: number; pageSize: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
}

/** A review as staff see it in the admin. */
export interface AdminProductReviewDTO {
  id: string;
  productId: string;
  productName: string;
  authorName: string;
  rating: number;
  title: string;
  body: string;
  status: ProductReviewStatus;
  createdAt: string;
  /** written by a customer on the product page (not entered by staff) */
  fromCustomer: boolean;
}
